// Libs
import React, { useCallback } from 'react';
import {
  Card as MuiCard,
  CardActionArea,
  CardMedia,
  CardContent,
  Box,
  Typography,
  Stack,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

// Components
import FlagButton from '../../Button/FlagButton';
import Rating from '../../Rating';
import CountDescription, { StockItem } from './CountDescription';
import NewLabel from './NewLabel';
import ReportableIcon from './ReportableIcon';
import Image from '../../Image';
import Details from '../Details';
import MaterialsProductQtyButtons from './MaterialsProductQtyButtons';
import FdmButton from './FdmButton';
import KitsProductQtyButtons from '../../Kits/KitsProductQtyButtons/KitsProductQtyButtons';

// Actions
import { OpenModal } from '../../../actions/modal';

// Hooks
import useCart from '../../../pages/Cart/hooks/useCart';
import useUser from '../../../hooks/useUser';
import useFavorites from '../../../hooks/useFavorites';
import useUnavailableForOrderProduct from '../../../hooks/useUnavailableForOrderProduct';

// Utils
import isDigitalAsset from '../../../utils/isDigitalAsset';
import canRateProduct from '../../../utils/canRateProduct';
import useUI from '../../../hooks/useUI';

import { useStyles } from './ListCardStyles';

const IMAGE_HEIGHT_DESKTOP = 275;
const IMAGE_HEIGHT_TABLET = 243;
const IMAGE_HEIGHT_MOBILE = 136;

const ListCard = ({
  product,
  cardContent,
  cardModalComponent,
  imageContainer,
  customCardSize = {},
  modalProps = {},
  nums,
}) => {
  const dispatch = useDispatch();
  const { selectingKitProducts, currentKit: kit } = useSelector(
    (state) => state.kits,
  );
  const { setFavoriteValue } = useFavorites();
  const { isDesktopMode, isTabletMode, isMobileMode } = useUI();
  const {
    image_link: image,
    product_description: description,
    product_id: stockNumber,
    pi_required: piRequired,
    unit_measure: unitMeasure,
    product_available_qty: availableQuantity,
    prepack_quantity: perUnitQty,
    order_limit: maxAllowedQty,
    is_new: isNew,
    is_favourite: isFavorite,
    reportable: isReportable,
    size_vers: sizeVers,
  } = product || {};
  const isRateable = canRateProduct(product);
  const { isKiteUser } = useUser();

  const {
    addInCart,
    addingProgress,
    count,
    addOne,
    removeOne,
    setCount,
    isProductInCart,
  } = useCart(stockNumber);
  const { isUnavailableForOrder, unavailableLabel, unavailableWatermark } =
    useUnavailableForOrderProduct(product);
  const isDigitalAssetProduct = isDigitalAsset(product);
  const { classes } = useStyles({
    isUnavailableForOrder,
    customCardSize,
    isKiteUser,
    isStacked: isMobileMode && availableQuantity <= 0,
  });

  const onOpenModal = useCallback(() => {
    dispatch(
      OpenModal({
        Component: cardModalComponent || Details,
        id: kit ? 'kits-product-details' : 'materials-product-details',
        props: {
          productId: stockNumber,
          isFullScreen: isTabletMode,
          ...modalProps,
          paddingContent: cardModalComponent
            ? {
                xl: '50px 40px 50px',
                lg: '60px 28px 30px',
                md: '40px 12px 16px',
              }
            : {
                xl: '50px 40px 40px',
                lg: '60px 28px 38px',
                md: '40px 12px 16px',
              },
        },
        styles: {
          width: 'min(90vw, 1143px)',
        },
      }),
    );
  }, [
    dispatch,
    cardModalComponent,
    kit,
    stockNumber,
    isTabletMode,
    modalProps,
  ]);
  const renderRating = () => {
    return (
      <Box
        alignItems="start"
        display="flex"
        justifyContent="space-between"
        width="100%"
        gap={1}>
        <Box className={classes.descAndSize}>
          <div
            onClick={onOpenModal}
            className={classes.description}
            data-testid="product-description">
            {description}
          </div>
          <Typography variant="textSm" className={classes.sizeVers}>
            {sizeVers}
          </Typography>
        </Box>
        {!isTabletMode && (
          <Box alignItems="center" display="flex" flexWrap="no-wrap">
            {isRateable && <Rating product={product} />}
            <FlagButton
              className={classes.flagButton}
              active={isFavorite}
              onClick={() => setFavoriteValue(product.product_id, !isFavorite)}
              id="flag-button"
            />
          </Box>
        )}
        {isTabletMode && !isMobileMode && (
          <FlagButton
            className={classes.flagButton}
            active={isFavorite}
            onClick={() => setFavoriteValue(product.product_id, !isFavorite)}
            id="flag-button"
          />
        )}
      </Box>
    );
  };
  const renderDetails = () => {
    return (
      <Box display="flex" justifyContent="space-between">
        {!isMobileMode && (
          <div className={classes.details}>
            <div
              className={classes.detailsItem}
              data-testid="product-card-stock-number">
              <span className={classes.detailsBold}>Stock Number:</span>
              <span className={classes.detailsNormal}>{stockNumber}</span>
            </div>
            <div
              className={classes.detailsItem}
              data-testid="product-card-pi-required">
              <span className={classes.detailsBold}>PI Required:</span>
              <span className={classes.detailsNormal}>
                {piRequired ? 'Yes' : 'No'}
              </span>
            </div>
            <div
              className={classes.detailsItem}
              data-testid="product-card-unit-measure">
              <span className={classes.detailsBold}>Unit Measure:</span>
              <span className={classes.detailsNormal}>{unitMeasure}</span>
            </div>
          </div>
        )}
        {isDesktopMode && isReportable && <ReportableIcon />}
        {isTabletMode && !isMobileMode && (
          <CountDescription
            perUnitQty={perUnitQty}
            maxAllowedQty={maxAllowedQty}
            availableQuantity={availableQuantity}
            unavailableLabel={unavailableLabel}
            isDigitalAsset={isDigitalAssetProduct}
          />
        )}
        {isTabletMode && !isMobileMode && (
          <Box flexBasis="5%" display="flex" justifyContent="end">
            {isReportable && <ReportableIcon />}
          </Box>
        )}
      </Box>
    );
  };
  const renderQtyButton = () => {
    return (
      <Box display="flex" justifyContent="space-between">
        <div className={classes.buttonsContainer}>
          {(availableQuantity > 0 || isDigitalAssetProduct) &&
            (selectingKitProducts || kit ? (
              <KitsProductQtyButtons
                product={product}
                listView
                detailsMode={false}
                isMobileMode={isMobileMode}
                nums={nums}
                className={classes.qtyButtons}
              />
            ) : (
              !isUnavailableForOrder && (
                <MaterialsProductQtyButtons
                  loading={addingProgress}
                  product={product}
                  addInCart={addInCart}
                  count={count}
                  addOne={addOne}
                  removeOne={removeOne}
                  setCount={setCount}
                  isProductInCart={isProductInCart}
                  listView
                  className={classes.qtyButtons}
                />
              )
            ))}
          {!isTabletMode && !isMobileMode && (
            <CountDescription
              perUnitQty={perUnitQty}
              maxAllowedQty={maxAllowedQty}
              availableQuantity={availableQuantity}
              unavailableLabel={unavailableLabel}
              isDigitalAsset={isDigitalAssetProduct}
            />
          )}
        </div>
        {!isMobileMode && (
          <div className={classes.qtyColumn}>
            <FdmButton product={product} className={classes.fdmButton} />
          </div>
        )}
      </Box>
    );
  };
  const renderTableModeRating = () => {
    return (
      isTabletMode &&
      !isMobileMode && (
        <Box
          alignItems="center"
          display="flex"
          flexWrap="no-wrap"
          className={classes.ratingContainer}>
          {isRateable && <Rating product={product} />}
        </Box>
      )
    );
  };
  return (
    <MuiCard disabled={!image} className={classes.root}>
      <div onClick={(e) => e.stopPropagation()} className={classes.labels}>
        {isNew && <NewLabel positionStatic />}
      </div>
      <CardActionArea className={classes.actionArea} onClick={onOpenModal}>
        {unavailableWatermark}
        <Image
          alt="productImage"
          src={image}
          imageContainer={
            imageContainer || {
              desktop: [IMAGE_HEIGHT_DESKTOP],
              tablet: [IMAGE_HEIGHT_TABLET],
              mobile: [IMAGE_HEIGHT_MOBILE],
            }
          }>
          <CardMedia
            id="image-button"
            className={classes.img}
            component="img"
            image={image}
            alt="productImage"
          />
        </Image>
      </CardActionArea>
      {!cardContent ? (
        <CardContent className={classes.content}>
          <div className={classes.stackedContent}>
            {isMobileMode && isRateable && <Rating product={product} />}
            {renderRating()}
            {renderTableModeRating()}
            {renderDetails()}
            {isMobileMode && (
              <Stack direction="row" justifyContent="space-between">
                <StockItem
                  availableQuantity={availableQuantity}
                  unavailableLabel={unavailableLabel}
                  isDigitalAsset={isDigitalAssetProduct}
                />
                {isReportable && <ReportableIcon />}
              </Stack>
            )}
          </div>
          {renderQtyButton()}
        </CardContent>
      ) : (
        cardContent
      )}
    </MuiCard>
  );
};

export default ListCard;
